import React, { useState } from "react";
import { ChevronUp, ChevronDown, GripVertical, Trash2, Pencil, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { UNITS } from "@/lib/customCalc";
import { CATEGORIES, PRICE_UNIT, defaultOrder, toRate } from "@/lib/itemPresets";
import { formatCurrency } from "@/lib/format";

const DEC = /^\d*\.?\d*$/;

function useCells(cfg, set, pi) {
  const p = cfg.periods[pi] || cfg.periods[0];
  const get = (ck, ik) => p.classes?.[ck]?.items?.[ik] || {};
  const write = (fn) => set((c) => ({ ...c, periods: c.periods.map((pp, i) => (i === pi ? fn(JSON.parse(JSON.stringify(pp))) : pp)) }));
  const setCell = (ck, it, field, val) => {
    if (!DEC.test(val)) return;
    write((pp) => {
      pp.classes = pp.classes || {}; pp.classes[ck] = pp.classes[ck] || { items: {} }; pp.classes[ck].items = pp.classes[ck].items || {};
      const cell = { qty: "", rate: "", ...(pp.classes[ck].items[it.key] || {}), [field]: val };
      if (it.cost_mode === "price") cell.rate = toRate(cell.qty, it.unit, cell.price);
      if (it.cost_mode === "free") cell.rate = 0;
      if (cell.qty === "" && (cell.rate === "" || it.cost_mode === "free") && !cell.price) delete pp.classes[ck].items[it.key];
      else pp.classes[ck].items[it.key] = cell;
      return pp;
    });
  };
  const copyClass = (from, toList, only) => write((pp) => {
    toList.forEach((to) => {
      if (to === from) return;
      pp.classes[to] = pp.classes[to] || { items: {} }; pp.classes[to].items = pp.classes[to].items || {};
      Object.entries(pp.classes[from]?.items || {}).forEach(([k, v]) => { if (!only || only === k) pp.classes[to].items[k] = { ...v }; });
    });
    return pp;
  });
  return { p, get, setCell, copyClass, write };
}

function CellInputs({ it, cell, onSet, tid, b }) {
  const valField = it.cost_mode === "price" ? "price" : "rate";
  return (
    <div>
      <div className="flex gap-1">
        <input className="input-thali h-10 px-1 w-full text-center font-num text-sm" inputMode="decimal" placeholder={b("qty")} value={cell.qty ?? ""} onChange={(e) => onSet("qty", e.target.value)} data-testid={`b-qty-${tid}`} />
        <input className="input-thali h-10 px-1 w-full text-center font-num text-sm disabled:opacity-40" inputMode="decimal" disabled={it.cost_mode === "free"}
          placeholder={it.cost_mode === "price" ? `₹/${PRICE_UNIT[it.unit]}` : "₹"} value={it.cost_mode === "free" ? "0" : (cell[valField] ?? "")} onChange={(e) => onSet(valField, e.target.value)} data-testid={`b-rate-${tid}`} />
      </div>
      {it.cost_mode === "price" && cell.qty !== undefined && cell.price !== undefined && (
        <div className="text-[10px] text-thali-ink/60 mt-0.5" data-testid={`b-conv-${tid}`}>{cell.qty || 0} {it.unit} × ₹{Number(cell.price || 0).toFixed(2)}/{PRICE_UNIT[it.unit]} = ₹{Number(cell.rate || 0).toFixed(4)}</div>
      )}
    </div>
  );
}

function ItemEditor({ it, set, b }) {
  const upd = (patch) => set((c) => ({ ...c, items: c.items.map((x) => (x.key === it.key ? { ...x, ...patch } : x)) }));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2" data-testid={`b-edit-${it.key}`}>
      <input className="input-thali h-10" value={it.name} maxLength={40} onChange={(e) => upd({ name: e.target.value })} data-testid={`b-item-name-${it.key}`} />
      <input className="input-thali h-10" placeholder={b("local_name")} value={it.name_local || ""} onChange={(e) => upd({ name_local: e.target.value })} />
      <select className="input-thali h-10 px-1" value={it.category || "Other"} onChange={(e) => upd({ category: e.target.value })}>{CATEGORIES.map((x) => <option key={x}>{x}</option>)}</select>
      <select className="input-thali h-10 px-1" value={it.unit} onChange={(e) => upd({ unit: e.target.value })} data-testid={`b-item-unit-${it.key}`}>{UNITS.map((x) => <option key={x}>{x}</option>)}</select>
      <select className="input-thali h-10 px-1" value={it.cost_mode || "rate"} onChange={(e) => upd({ cost_mode: e.target.value })} data-testid={`b-item-mode-${it.key}`}>
        <option value="rate">{b("cm_rate")}</option><option value="price">{b("cm_price")}</option><option value="free">{b("cm_free")}</option>
      </select>
      <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" className="w-5 h-5 accent-thali-green" checked={!!it.grain} onChange={(e) => upd({ grain: e.target.checked })} /> 🌾 {b("grain")}</label>
    </div>
  );
}

export function removeItems(c, keys) {
  const menu = Object.fromEntries(Object.entries(c.menu).map(([wd, m]) => [wd, { ...m, items: (m.items || []).filter((k) => !keys.includes(k)) }]));
  const periods = c.periods.map((p) => ({ ...p, auto_balance_item: keys.includes(p.auto_balance_item) ? null : p.auto_balance_item,
    classes: Object.fromEntries(Object.entries(p.classes || {}).map(([ck, cr]) => [ck, { ...cr, items: Object.fromEntries(Object.entries(cr.items || {}).filter(([k]) => !keys.includes(k))) }])) }));
  return { ...c, items: c.items.filter((i) => !keys.includes(i.key)), menu, periods };
}

export default function ItemLibrary({ cfg, set, b, pi }) {
  const { p, get, setCell, copyClass, write } = useCells(cfg, set, pi);
  const [editing, setEditing] = useState(null);
  const [card, setCard] = useState(0);
  const [drag, setDrag] = useState(null);
  const [delGroup, setDelGroup] = useState(null);
  const [colFrom, setColFrom] = useState(cfg.classes[0]?.key);
  const [colTo, setColTo] = useState(cfg.classes[1]?.key || "");
  const groups = cfg.groups || [];
  const items = cfg.items;
  const move = (from, to) => set((c) => { const a = [...c.items]; const [x] = a.splice(from, 1); a.splice(Math.max(0, Math.min(a.length, to)), 0, x); return { ...c, items: a }; });
  const gOf = (k) => groups.find((g) => g.key === k);
  const groupTotal = (gk, ck) => items.filter((i) => i.group === gk).reduce((s, i) => s + Number(get(ck, i.key).rate || 0), 0);
  const classTotal = (ck) => items.reduce((s, i) => s + Number(get(ck, i.key).rate || 0), 0);
  const ab = p.auto_balance_item;
  const updGroup = (gk, patch) => set((c) => ({ ...c, groups: c.groups.map((g) => (g.key === gk ? { ...g, ...patch } : g)) }));
  const doDelGroup = (all) => {
    const gk = delGroup;
    setDelGroup(null);
    set((c) => {
      const keys = c.items.filter((i) => i.group === gk).map((i) => i.key);
      const base = all ? removeItems(c, keys) : { ...c, items: c.items.map((i) => (i.group === gk ? { ...i, group: undefined } : i)) };
      return { ...base, groups: (c.groups || []).filter((g) => g.key !== gk) };
    });
  };
  if (!items.length) return null;

  const Head = ({ it, idx }) => {
    const prev = items[idx - 1];
    if (!it.group || prev?.group === it.group) return null;
    const g = gOf(it.group) || { key: it.group, name: it.group, emoji: "🧁" };
    const pc = g.piece_cost;
    const tot = groupTotal(g.key, cfg.classes[0].key);
    return (
      <div className="rounded-xl bg-[#FFF4E6] p-2 mt-2 flex flex-wrap items-center gap-2" data-testid={`b-group-${g.key}`}>
        <span className="font-heading font-bold flex-1">{g.emoji} {g.name}</span>
        <input className="input-thali h-9 w-24 text-center text-sm" inputMode="decimal" placeholder={b("piece_cost")} value={pc ?? ""}
          onChange={(e) => DEC.test(e.target.value) && updGroup(g.key, { piece_cost: e.target.value === "" ? null : e.target.value })} data-testid={`b-group-cost-${g.key}`} />
        {pc !== null && pc !== undefined && pc !== "" && (
          Math.abs(tot - Number(pc)) < 0.005
            ? <span className="text-xs font-bold text-thali-green" data-testid={`b-group-check-${g.key}`}>{b("sub_total")} {formatCurrency(tot)} ✓</span>
            : <span className="text-xs font-bold text-amber-700" data-testid={`b-group-check-${g.key}`}>⚠️ {b("sub_total")} {formatCurrency(tot)} ≠ {formatCurrency(Number(pc))}</span>
        )}
        <button onClick={() => setDelGroup(g.key)} className="text-red-600 p-2" aria-label={b("remove")} data-testid={`b-group-del-${g.key}`}><Trash2 className="w-4 h-4" /></button>
        {delGroup === g.key && (
          <div className="w-full flex gap-2" data-testid="b-group-del-confirm">
            <span className="text-xs flex-1">{b("del_group_q").replace("{n}", items.filter((i) => i.group === g.key).length)}</span>
            <button onClick={() => doDelGroup(true)} className="text-xs font-bold bg-red-600 text-white rounded-lg px-2 py-1" data-testid="b-group-del-all">{b("del_all")}</button>
            <button onClick={() => doDelGroup(false)} className="text-xs font-bold bg-white rounded-lg px-2 py-1 border" data-testid="b-group-keep">{b("keep_items")}</button>
          </div>
        )}
      </div>
    );
  };

  const RowTools = ({ it, idx }) => (
    <div className="flex items-center gap-0.5 shrink-0">
      <button onClick={() => move(idx, idx - 1)} className="p-1.5 text-thali-ink/50" aria-label="up" data-testid={`b-up-${it.key}`}><ChevronUp className="w-4 h-4" /></button>
      <button onClick={() => move(idx, idx + 1)} className="p-1.5 text-thali-ink/50" aria-label="down" data-testid={`b-down-${it.key}`}><ChevronDown className="w-4 h-4" /></button>
      <button onClick={() => setEditing(editing === it.key ? null : it.key)} className="p-1.5 text-thali-ink/50" aria-label={b("edit")} data-testid={`b-edit-btn-${it.key}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => copyClass(cfg.classes[0].key, cfg.classes.map((c) => c.key), it.key)} className="p-1.5 text-thali-green" title={b("same_all")} aria-label={b("same_all")} data-testid={`b-same-${it.key}`}><Copy className="w-4 h-4" /></button>
      <button onClick={() => set((c) => removeItems(c, [it.key]))} className="p-1.5 text-red-600" aria-label={b("remove")} data-testid={`b-item-remove-${it.key}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
  const label = (it) => <span className="text-sm font-semibold truncate">{it.group ? "↳ " : ""}{it.emoji} {it.name || "—"} <span className="text-[10px] text-thali-ink/40">{it.unit}{it.cost_mode === "free" ? ` · ${b("cm_free")}` : ""}{ab === it.key ? " · ⚖️" : ""}</span></span>;
  const ci = Math.min(card, items.length - 1);

  return (
    <div className="space-y-3" data-testid="b-library">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => copyClass(cfg.classes[0].key, cfg.classes.map((c) => c.key))} className="btn-ghost text-xs px-3 border border-thali-turmeric/40" data-testid="b-copy-first-all"><Copy className="w-3 h-3" /> {b("copy_first_all").replace("{c}", cfg.classes[0].name)}</button>
        <button onClick={() => set((c) => ({ ...c, items: defaultOrder(c.items) }))} className="btn-ghost text-xs px-3 border border-thali-turmeric/40" data-testid="b-default-order">↕ {b("default_order")}</button>
        {cfg.classes.length > 1 && (
          <div className="flex items-center gap-1 text-xs">
            <select className="input-thali h-9 px-1 text-xs w-28" value={colFrom} onChange={(e) => setColFrom(e.target.value)} data-testid="b-col-from">{cfg.classes.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}</select>→
            <select className="input-thali h-9 px-1 text-xs w-28" value={colTo} onChange={(e) => setColTo(e.target.value)} data-testid="b-col-to">{cfg.classes.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}</select>
            <button onClick={() => copyClass(colFrom, [colTo])} className="btn-ghost text-xs px-2 border border-thali-turmeric/40" data-testid="b-col-copy">{b("copy_column")}</button>
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <div className="grid gap-2 text-[11px] font-bold text-thali-ink/50 px-1" style={{ gridTemplateColumns: `minmax(150px,1.2fr) repeat(${cfg.classes.length}, minmax(120px,1fr)) 160px` }}>
          <span>{b("item_name")}</span>{cfg.classes.map((c) => <span key={c.key} style={{ color: c.color }}>{c.emoji} {c.name} · {b("qty")} | ₹</span>)}<span />
        </div>
        {items.map((it, idx) => (
          <React.Fragment key={it.key}>
            {Head({ it, idx })}
            <div draggable onDragStart={() => setDrag(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) move(drag, idx); setDrag(null); }}
              className={`grid gap-2 items-start py-1.5 px-1 border-b border-thali-turmeric/10 ${it.group ? "bg-[#FFF4E6]/60" : ""}`}
              style={{ gridTemplateColumns: `minmax(150px,1.2fr) repeat(${cfg.classes.length}, minmax(120px,1fr)) 160px` }} data-testid={`b-row-${it.key}`}>
              <div className="flex items-center gap-1 min-w-0 pt-2"><GripVertical className="w-4 h-4 text-thali-ink/30 cursor-grab shrink-0" />{label(it)}</div>
              {cfg.classes.map((c) => <CellInputs key={c.key} it={it} b={b} cell={get(c.key, it.key)} onSet={(f, v) => setCell(c.key, it, f, v)} tid={`${c.key}-${it.key}`} />)}
              {RowTools({ it, idx })}
            </div>
            {editing === it.key && <ItemEditor it={it} set={set} b={b} />}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile: one item card at a time */}
      <div className="md:hidden space-y-2" data-testid="b-mobile-card">
        <div className="flex items-center justify-between">
          <button onClick={() => setCard(Math.max(0, ci - 1))} className="btn-ghost px-3" aria-label="prev" data-testid="b-card-prev"><ChevronLeft className="w-4 h-4" /></button>
          <select className="input-thali h-11 flex-1 mx-2 text-sm" value={ci} onChange={(e) => setCard(Number(e.target.value))} data-testid="b-card-select">
            {items.map((it, i) => <option key={it.key} value={i}>{`${i + 1}/${items.length} · ${it.group ? `${gOf(it.group)?.name || ""} › ` : ""}${it.name}`}</option>)}
          </select>
          <button onClick={() => setCard(Math.min(items.length - 1, ci + 1))} className="btn-ghost px-3" aria-label="next" data-testid="b-card-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
        {(() => {
          const it = items[ci];
          return (
            <div className="rounded-2xl bg-white border border-thali-turmeric/30 p-3 space-y-2">
              {it.group && <div className="text-xs font-bold text-thali-saffron">{gOf(it.group)?.emoji} {gOf(it.group)?.name}</div>}
              <div className="flex items-center justify-between gap-1">{label(it)}{RowTools({ it, idx: ci })}</div>
              {editing === it.key && <ItemEditor it={it} set={set} b={b} />}
              {cfg.classes.map((c) => (
                <div key={c.key} className="grid grid-cols-[90px_1fr] gap-2 items-start">
                  <span className="text-xs font-semibold pt-3 truncate" style={{ color: c.color }}>{c.emoji} {c.name}</span>
                  <CellInputs it={it} b={b} cell={get(c.key, it.key)} onSet={(f, v) => setCell(c.key, it, f, v)} tid={`m-${c.key}-${it.key}`} />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="rounded-2xl bg-thali-green/10 border border-thali-green/30 p-3 space-y-2" data-testid="b-balance">
        <label className="text-sm font-semibold">⚖️ {b("auto_balance")}</label>
        <select className="input-thali" value={ab || ""} onChange={(e) => write((pp) => ({ ...pp, auto_balance_item: e.target.value || null }))} data-testid="b-ab-item">
          <option value="">{b("none")}</option>{items.filter((i) => !i.group).map((it) => <option key={it.key} value={it.key}>{it.emoji} {it.name}</option>)}
        </select>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {cfg.classes.map((c) => (
            <div key={c.key} className="rounded-xl bg-white p-2" data-testid={`b-total-${c.key}`}>
              <div className="text-[11px] font-semibold truncate" style={{ color: c.color }}>{c.emoji} {c.name}</div>
              <div className="font-num font-bold">{formatCurrency(classTotal(c.key))}</div>
              <div className="text-[10px] text-thali-ink/50">{b("cost_all_items")}</div>
              {ab && (
                <input className="input-thali h-9 mt-1 text-center text-sm" inputMode="decimal" placeholder={b("target")} value={p.classes?.[c.key]?.target_total ?? ""}
                  onChange={(e) => DEC.test(e.target.value) && write((pp) => { pp.classes = pp.classes || {}; pp.classes[c.key] = { items: {}, ...(pp.classes[c.key] || {}), target_total: e.target.value }; return pp; })} data-testid={`b-target-${c.key}`} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
